/**
 * 检查元素是否显示省略号（支持单行和多行文本）
 * @param element 要检查的DOM元素
 * @param lineClamp 最大行数（默认为1，单行）
 * @param tolerance 容差（像素，默认为5px）
 * @returns 如果文本溢出并显示省略号则返回true，否则返回false
 */
export const checkIfEllipsisActive = (
  element: HTMLElement,
  lineClamp: number = 1,
  tolerance: number = 5
): boolean => {
  if (!element) return false;

  // 方法1：对于单行文本，使用scrollWidth和clientWidth比较
  if (lineClamp === 1) {
    return element.scrollWidth > element.clientWidth + tolerance;
  }

  // 方法2：对于多行文本，使用高度比较法
  const style = window.getComputedStyle(element);

  // 获取行高
  const lineHeight = parseInt(style.lineHeight, 10) || 20; // 默认20px

  // 获取上下内边距
  const paddingTop = parseInt(style.paddingTop, 10) || 0;
  const paddingBottom = parseInt(style.paddingBottom, 10) || 0;

  // 计算指定行数的理论最大高度
  const maxHeight = lineHeight * lineClamp + paddingTop + paddingBottom;

  // 计算实际内容高度（创建一个临时元素来获取不限制行数的实际高度）
  const tempDiv = document.createElement('div');
  tempDiv.style.cssText = `
    position: absolute;
    visibility: hidden;
    top: -9999px;
    left: -9999px;
    width: ${element.offsetWidth}px;
    font-size: ${style.fontSize};
    line-height: ${style.lineHeight};
    font-family: ${style.fontFamily};
    box-sizing: ${style.boxSizing};
    white-space: normal;
    word-wrap: break-word;
    word-break: break-all;
    padding: ${style.padding};
    border: ${style.border};
  `;

  tempDiv.textContent = element.textContent || '';
  document.body.appendChild(tempDiv);
  const actualHeight = tempDiv.offsetHeight;
  document.body.removeChild(tempDiv);

  // 比较实际高度和理论最大高度
  return actualHeight > maxHeight + tolerance;
};

/**
 * 检查所有指定类名的元素是否显示省略号
 * @param selector CSS选择器或HTMLElement数组
 * @param lineClamp 最大行数
 * @param tolerance 容差
 * @returns 包含元素和是否溢出状态的数组
 */
export const checkElementsEllipsis = (
  selector: string | HTMLElement[],
  lineClamp: number = 1,
  tolerance: number = 5
): Array<{ element: HTMLElement; isOverflow: boolean }> => {
  let elements: HTMLElement[] = [];

  if (typeof selector === 'string') {
    elements = Array.from(document.querySelectorAll<HTMLElement>(selector));
  } else {
    elements = selector;
  }

  return elements.map(element => ({
    element,
    isOverflow: checkIfEllipsisActive(element, lineClamp, tolerance)
  }));
};

/**
 * 异步检查省略号状态（等待DOM更新）
 * @param selector CSS选择器或HTMLElement数组
 * @param lineClamp 最大行数
 * @param delay 延迟时间（毫秒）
 * @returns Promise
 */
export const checkEllipsisAsync = async (
  selector: string | HTMLElement[],
  lineClamp: number = 1,
  delay: number = 300
): Promise<Array<{ element: HTMLElement; isOverflow: boolean }>> => {
  return new Promise(resolve => {
    setTimeout(() => {
      const result = checkElementsEllipsis(selector, lineClamp);
      resolve(result);
    }, delay);
  });
};